import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useContext, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import FailedLoading from "../../../app/common/FailedLoading/FailedLoading";
import Loading from "../../../app/layout/Loading/Loading";
import { RootStoreContext } from "../../../app/stores/rootStore";
import ProjectSettings from "./ProjectSettings/ProjectSettings";
import ProjectHome from "./ProjectHome/ProjectHome";
import ProjectCompounds from "./ProjectCompounds/ProjectCompounds";
import PatentCompounds from "./PatentCompounds/PatentCompounds";
import ReferenceCompounds from "./ReferenceCompounds/ReferenceCompounds";
import SubTableView from "./SubTableView/SubTableView";
import EvolutionViewG6 from "./EvolutionView/EvolutionViewG6";
import GroupSidebar from "../../../app/common/GroupSidebar/GroupSidebar";
import Screening from "./Screening/Screening";
import { AIChatProvider } from "../../../app/common/AIChat/AIChatContext";
import AIChatPanel from "../../../app/common/AIChat/AIChatPanel";
import AIChatButton from "../../../app/common/AIChat/AIChatButton";
import agent from "../../../app/api/agent";

const ProjectView = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const rootStore = useContext(RootStoreContext);
  const { loadingProject, fetchProject, selectedProject } =
    rootStore.projectStore;
  const { patentCompoundStore, compoundStore } = rootStore;

  // ── Custom dataset create dialog state ──
  const [showCreateDs, setShowCreateDs] = useState(false);
  const [newDsName, setNewDsName] = useState("");
  const [dsFilterId, setDsFilterId] = useState("");
  const [dsFilterSynth, setDsFilterSynth] = useState("");
  const [dsFilterMwMin, setDsFilterMwMin] = useState("");
  const [dsFilterMwMax, setDsFilterMwMax] = useState("");
  const [dsFilterLogpMin, setDsFilterLogpMin] = useState("");
  const [dsFilterLogpMax, setDsFilterLogpMax] = useState("");

  const resetDsForm = () => {
    setNewDsName("");
    setDsFilterId("");
    setDsFilterSynth("");
    setDsFilterMwMin("");
    setDsFilterMwMax("");
    setDsFilterLogpMin("");
    setDsFilterLogpMax("");
  };

  const handleCreateDataset = async () => {
    if (!newDsName.trim()) return;
    const filterObj = {};
    if (dsFilterId.trim()) filterObj.externalCompoundIds = dsFilterId.trim();
    if (dsFilterSynth) filterObj.synthesized = dsFilterSynth;
    if (dsFilterMwMin || dsFilterMwMax) {
      filterObj.molWeight = {};
      if (dsFilterMwMin) filterObj.molWeight.min = parseFloat(dsFilterMwMin);
      if (dsFilterMwMax) filterObj.molWeight.max = parseFloat(dsFilterMwMax);
    }
    if (dsFilterLogpMin || dsFilterLogpMax) {
      filterObj.molLogP = {};
      if (dsFilterLogpMin) filterObj.molLogP.min = parseFloat(dsFilterLogpMin);
      if (dsFilterLogpMax) filterObj.molLogP.max = parseFloat(dsFilterLogpMax);
    }
    try {
      await compoundStore.createDataSet({
        projectId: params.id,
        name: newDsName.trim(),
        filterJson: JSON.stringify(filterObj),
      });
      setShowCreateDs(false);
      resetDsForm();
    } catch (err) {
      console.error("Failed to create dataset", err);
    }
  };

  useEffect(() => {
    if (selectedProject === null || selectedProject.id !== params.id) {
      fetchProject(params.id);
    }
  }, [params.id, selectedProject, fetchProject]);

  // Fetch patent groups & compound datasets for sidebar
  useEffect(() => {
    if (params.id) {
      patentCompoundStore.fetchGroups(params.id);
      compoundStore.fetchDataSets(params.id);
    }
  }, [params.id, patentCompoundStore, compoundStore]);

  /** Loading Overlay */
  if (loadingProject) {
    return <Loading />;
  }

  if (
    !loadingProject &&
    selectedProject !== null &&
    selectedProject.id === params.id
  ) {
    // Build sidebar items: All (root) > Synthesized + sub-tables + user datasets
    const dsParam = searchParams.get("ds");
    const subTableParam = searchParams.get("sub");
    const userSets = (compoundStore.compoundDatasets || []).filter((d) => !d.isBuiltIn);
    const compoundSidebarItems = [
      {
        id: "all",
        label: "Compounds",
        children: [
          { id: "__all__", label: "All" },
          { id: "__design__", label: "🎯 Design", subTable: true },
          { id: "__evolution__", label: "🔗 Evolution", subTable: true },
          { id: "__synthesis__", label: "⚗️ Synthesis", subTable: true },
          { id: "__activity__", label: "🔬 Activity", subTable: true },
          { id: "__dmpk__", label: "📈 DMPK", subTable: true },
          { id: "__asset__", label: "📦 Assets", subTable: true },
          { id: "__other__", label: "📋 Other", subTable: true },
          ...userSets.map((d) => ({ id: d.id, label: d.name, deletable: true })),
        ],
      },
    ];

    const onCompoundSelect = (item) => {
      if (item.subTable) {
        const tableType = item.id.replace(/^__/, "").replace(/__$/, "");
        navigate(`compounds/?sub=${tableType}`);
      } else if (item.id === "__all__") {
        navigate("compounds/");
      } else {
        navigate(`compounds/?ds=${item.id}`);
      }
    };

    const compoundSelectedId = location.pathname.includes("compounds")
      ? subTableParam
        ? `__${subTableParam}__`
        : dsParam || "__all__"
      : null;

    const isScreening = location.pathname.includes("/screening/");
    const isHomePage = !location.pathname.includes("/compounds/") && !location.pathname.includes("/patent-compounds/") && !location.pathname.includes("/reference-compounds/") && !location.pathname.includes("/settings/") && !isScreening;
    const showSidebar = !isHomePage && !isScreening;
    const isRefActive = location.pathname.includes("reference-compounds");
    const isPatentActive = location.pathname.includes("patent-compounds");

    return (
      <AIChatProvider projectId={params.id}>
      <div className={isHomePage || isScreening ? "flex w-full" : "flex gap-2 w-full"} style={{ flex: 1, minHeight: 0 }}>
        {/* ── Left Sidebar (hidden on home page and screening page) ── */}
        {showSidebar && (
        <div
          className="flex flex-column"
          style={{
            width: 220,
            minWidth: 220,
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            background: "#fafbfc",
            padding: "8px 0",
            alignSelf: "flex-start",
          }}
        >
          {/* Section: Project Settings */}
          <div style={{ padding: "0 8px", marginBottom: 4 }}>
            <div
              className="flex align-items-center gap-2 px-2 py-2 border-round cursor-pointer hover:bg-50 transition-colors"
              style={{
                fontSize: "0.875rem",
                borderRadius: 6,
                background: location.pathname.includes("settings") ? "#eef2ff" : "transparent",
                color: location.pathname.includes("settings") ? "#4f46e5" : "#374151",
                fontWeight: location.pathname.includes("settings") ? 600 : 400,
                transition: "all 0.15s ease",
              }}
              onClick={() => navigate("compounds/")}
            >
              <i className="icon icon-common icon-briefcase" style={{ fontSize: "0.85rem" }} />
              <span>Dashboard</span>
              {location.pathname.includes("compounds") && (
                <span style={{ marginLeft: "auto", width: 3, height: 16, background: "#4f46e5", borderRadius: 2 }} />
              )}
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 12px" }} />

          <GroupSidebar
            title="Compounds"
            icon="icon icon-common icon-math"
            items={compoundSidebarItems}
            selectedId={compoundSelectedId}
            onSelect={onCompoundSelect}
            onCreate={async () => {}}
            onOpenCreate={() => {
              resetDsForm();
              setShowCreateDs(true);
            }}
            onDelete={async (item) => {
              await compoundStore.deleteDataSet(item.id);
              if (dsParam === item.id) {
                navigate("compounds/");
              }
            }}
            mergeHeader
            createIndent
            createLabel="Filtered Dataset"
          />
          {/* Reference Compounds - simple link (no groups) */}
          <div style={{ padding: "0 8px", marginBottom: 4 }}>
            <div
              className="flex align-items-center gap-2 px-2 py-2 border-round cursor-pointer hover:bg-50 transition-colors"
              style={{
                fontSize: "0.875rem",
                borderRadius: 6,
                background: isRefActive ? "#eef2ff" : "transparent",
                color: isRefActive ? "#4f46e5" : "#374151",
                fontWeight: isRefActive ? 600 : 400,
                transition: "all 0.15s ease",
              }}
              onClick={() => navigate("reference-compounds/")}
            >
              <i className="icon icon-common icon-book" style={{ fontSize: "0.85rem" }} />
              <span>Reference Compounds</span>
              {isRefActive && (
                <span style={{ marginLeft: "auto", width: 3, height: 16, background: "#4f46e5", borderRadius: 2 }} />
              )}
            </div>
          </div>
          <GroupSidebar
            title="Patent Compounds"
            icon="icon icon-common icon-copy"
            items={
              patentCompoundStore.groups.length > 0
                ? [
                    {
                      id: "__patent_root__",
                      label: "Patent Compounds",
                      children: patentCompoundStore.groups.map((g) => ({ id: g.id, label: g.name, deletable: true })),
                    },
                  ]
                : []
            }
            selectedId={isPatentActive ? patentCompoundStore.selectedGroupId : null}
            itemCount={patentCompoundStore.groups.length}
            onSelect={(item) => {
              if (item.id === "__patent_root__") return;
              patentCompoundStore.selectGroup(item.id);
              navigate("patent-compounds/");
            }}
            onCreate={async (name) => {
              await patentCompoundStore.createGroup(params.id, name);
            }}
            onDelete={async (item) => {
              await patentCompoundStore.deleteGroup(item.id, params.id);
            }}
            mergeHeader
            createIndent
            createLabel="New Group"
            createDialogTitle="New Group"
          />

          {/* Divider */}
          <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 12px" }} />

          {/* Screening - simple link */}
          <div style={{ padding: "0 8px", marginBottom: 4 }}>
            <div
              className="flex align-items-center gap-2 px-2 py-2 border-round cursor-pointer hover:bg-50 transition-colors"
              style={{
                fontSize: "0.875rem",
                borderRadius: 6,
                background: isScreening ? "#eef2ff" : "transparent",
                color: isScreening ? "#4f46e5" : "#374151",
                fontWeight: isScreening ? 600 : 400,
                transition: "all 0.15s ease",
              }}
              onClick={() => navigate("screening/")}
            >
              <i className="icon icon-common icon-lab" style={{ fontSize: "0.85rem" }} />
              <span>Screening</span>
              {isScreening && (
                <span style={{ marginLeft: "auto", width: 3, height: 16, background: "#4f46e5", borderRadius: 2 }} />
              )}
            </div>
          </div>
        </div>
        )}
        {/* ── Main Content Area ── */}
        <div className="flex w-full" style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}>
          <Routes>
            <Route index element={<ProjectHome project={selectedProject} />} />
            <Route
              path="settings/"
              element={
                <ProjectSettings id={params.id} project={selectedProject} />
              }
            />
            <Route
              path="compounds/"
              element={
                subTableParam === "evolution" ? (
                  <EvolutionViewG6 projectId={params.id} />
                ) : subTableParam ? (
                  <SubTableView key={subTableParam} tableType={subTableParam} projectId={params.id} />
                ) : (
                  <ProjectCompounds id={params.id} />
                )
              }
            />
            <Route
              path="patent-compounds/"
              element={
                <PatentCompounds id={params.id} />
              }
            />
            <Route
              path="reference-compounds/"
              element={
                <ReferenceCompounds id={params.id} />
              }
            />
            <Route
              path="screening/*"
              element={
                <Screening />
              }
            />
          </Routes>
        </div>

        {/* ── Create Dataset Dialog ── */}
        <Dialog
          header="Create New Dataset"
          visible={showCreateDs}
          style={{ width: "480px" }}
          modal
          onHide={() => { setShowCreateDs(false); resetDsForm(); }}
          footer={
            <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
              <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => { setShowCreateDs(false); resetDsForm(); }} />
              <Button label="Create" icon="pi pi-check" onClick={handleCreateDataset} disabled={!newDsName.trim()} style={{
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                border: "none", color: "#fff", borderRadius: 6, padding: "8px 16px",
                boxShadow: "0 2px 6px rgba(99,102,241,0.25)", fontWeight: 500,
              }} />
            </div>
          }
        >
          <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
            {/* Name */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                Dataset Name *
              </label>
              <InputText
                value={newDsName}
                onChange={(e) => setNewDsName(e.target.value)}
                placeholder="e.g. Potent compounds"
                style={{ width: "100%" }}
                autoFocus
              />
            </div>

            <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }} />
            <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>Filter Conditions</div>

            {/* ID contains */}
            <div>
              <label style={{ fontSize: 12, color: "#475569", display: "block", marginBottom: 2 }}>ID contains</label>
              <InputText
                value={dsFilterId}
                onChange={(e) => setDsFilterId(e.target.value)}
                placeholder="Text to search..."
                style={{ width: "100%" }}
              />
            </div>

            {/* SYNTHESIZED */}
            <div>
              <label style={{ fontSize: 12, color: "#475569", display: "block", marginBottom: 2 }}>SYNTHESIZED</label>
              <Dropdown
                value={dsFilterSynth}
                options={[
                  { label: "All", value: "" },
                  { label: "YES", value: "YES" },
                  { label: "NO", value: "NO" },
                ]}
                onChange={(e) => setDsFilterSynth(e.value)}
                style={{ width: "100%" }}
              />
            </div>

            {/* MW range */}
            <div>
              <label style={{ fontSize: 12, color: "#475569", display: "block", marginBottom: 2 }}>MW range</label>
              <div className="flex align-items-center gap-2">
                <InputText
                  value={dsFilterMwMin}
                  onChange={(e) => setDsFilterMwMin(e.target.value)}
                  placeholder="Min"
                  style={{ flex: 1 }}
                  keyfilter="num"
                />
                <span style={{ color: "#94a3b8" }}>~</span>
                <InputText
                  value={dsFilterMwMax}
                  onChange={(e) => setDsFilterMwMax(e.target.value)}
                  placeholder="Max"
                  style={{ flex: 1 }}
                  keyfilter="num"
                />
              </div>
            </div>

            {/* LogP range */}
            <div>
              <label style={{ fontSize: 12, color: "#475569", display: "block", marginBottom: 2 }}>LogP range</label>
              <div className="flex align-items-center gap-2">
                <InputText
                  value={dsFilterLogpMin}
                  onChange={(e) => setDsFilterLogpMin(e.target.value)}
                  placeholder="Min"
                  style={{ flex: 1 }}
                  keyfilter="num"
                />
                <span style={{ color: "#94a3b8" }}>~</span>
                <InputText
                  value={dsFilterLogpMax}
                  onChange={(e) => setDsFilterLogpMax(e.target.value)}
                  placeholder="Max"
                  style={{ flex: 1 }}
                  keyfilter="num"
                />
              </div>
            </div>
          </div>
        </Dialog>
      </div>
      <AIChatPanel />
      <AIChatButton />
    </AIChatProvider>
    );
  }

  return <FailedLoading />;
};

export default observer(ProjectView);
